/**
 * ButtonStyleSections — Collapsible Accordion groups for V2 button style fields
 *
 * Modeled after ListStyleSections.tsx. Renders each ButtonStyleGroup as a
 * collapsible MUI Accordion containing a 2-column grid of StyleFieldRows.
 *
 * For icon groups (iconUpload: true), renders MediaUploadField + settings
 * instead of standard grid.
 */

import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MediaUploadField } from '@modules/ui-components';
import { StyleFieldRow } from './StyleFieldRow';
import { PaddingElement, type PaddingValues } from '../../../factories/elements/PaddingElement';
import type { ButtonStyleGroup } from '../../../types/editorFieldTypes';
import { FIELD_STYLE_GRID_GAP } from '../../../config/ui';
import { EXPANDED_SECTION_BGCOLOR } from '../../../config/editorFieldStyles';
import { getStyleInputTestId, getStyleCheckboxTestId } from '../../../utils/testIdUtils';

export interface ButtonStyleSectionsProps {
  /** Button style group configurations */
  sections: ButtonStyleGroup[];

  /** Current style values (flat map: path → value) */
  values: Record<string, unknown>;

  /** Callback when a value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether editing is disabled */
  disabled?: boolean;

  /** Current locale for icon upload */
  locale: string;

  /** Optional upload handler for icon image files */
  onIconUpload?: (file: File) => Promise<string>;
}

/**
 * Extract icon URL from LocalizedImage value in values map.
 * LocalizedImage = { translations: { [locale]: AssetReference } }
 * AssetReference = { original?: string, small?: string }
 */
function extractIconUrl(
  values: Record<string, unknown>,
  iconPath: string,
  locale: string,
): string {
  const iconValue = values[iconPath];
  if (!iconValue || typeof iconValue !== 'object') return '';
  const translations = (iconValue as Record<string, unknown>).translations as
    | Record<string, Record<string, string>>
    | undefined;
  if (!translations) return '';
  const assetRef = translations[locale];
  if (!assetRef) return '';
  return assetRef.original ?? assetRef.small ?? '';
}

/**
 * Get padding values from flat values map at given base path.
 */
function getPaddingValues(
  values: Record<string, unknown>,
  basePath: string,
): PaddingValues {
  return {
    paddingTop: typeof values[`${basePath}.top`] === 'number' ? (values[`${basePath}.top`] as number) : 0,
    paddingRight: typeof values[`${basePath}.end`] === 'number' ? (values[`${basePath}.end`] as number) : 0,
    paddingBottom: typeof values[`${basePath}.bottom`] === 'number' ? (values[`${basePath}.bottom`] as number) : 0,
    paddingLeft: typeof values[`${basePath}.start`] === 'number' ? (values[`${basePath}.start`] as number) : 0,
  };
}

export const ButtonStyleSections: React.FC<ButtonStyleSectionsProps> = ({
  sections,
  values,
  onChange,
  disabled = false,
  locale,
  onIconUpload,
}) => {
  const theme = useTheme();

  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      // PaddingElement writes V1 paths (.paddingTop etc.) — translate to V2 EdgeInsets (.top etc.)
      const translatedPath = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(translatedPath, value);
    },
    [onChange],
  );

  /** Handle MediaUploadField changes for icon */
  const handleIconMediaChange = useCallback(
    (iconPath: string, newValue: string | File | null) => {
      if (newValue === null) {
        onChange(iconPath, null);
      } else if (newValue instanceof File) {
        if (onIconUpload) {
          onIconUpload(newValue).then((url) => {
            onChange(iconPath, {
              translations: { [locale]: { original: url, small: url } },
            });
          });
        }
      } else {
        // URL string
        if (!newValue) {
          onChange(iconPath, null);
        } else {
          onChange(iconPath, {
            translations: {
              [locale]: { original: newValue, small: newValue },
            },
          });
        }
      }
    },
    [onChange, onIconUpload, locale],
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
      data-testid="button-style-sections"
    >
      {sections.map((section) => {
        // For icon groups, find the imageUpload field and non-imageUpload fields
        const iconField = section.iconUpload
          ? section.fields.find((f) => f.type === 'imageUpload')
          : undefined;
        const regularFields = section.iconUpload
          ? section.fields.filter((f) => f.type !== 'imageUpload')
          : section.fields;

        return (
          <Accordion
            key={section.id}
            defaultExpanded={section.defaultExpanded}
            disableGutters
            elevation={0}
            data-testid={`button-style-section-${section.id}`}
            sx={{
              backgroundColor: 'transparent',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 32,
                px: 1,
                py: 0,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
              },
              '& .MuiAccordionSummary-content': {
                margin: '4px 0',
              },
              '& .MuiAccordionDetails-root': {
                px: 2,
                pt: 1,
                pb: 1,
                bgcolor: EXPANDED_SECTION_BGCOLOR,
                borderRadius: 1,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
              aria-controls={`${section.id}-content`}
              id={`${section.id}-header`}
            >
              <Typography variant="caption" fontWeight={500}>
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Custom component override (e.g., GradientEditor, ShadowEditor, EffectsEditor, IconEditor) */}
              {section.customComponent ? (
                <section.customComponent
                  values={values}
                  onChange={onChange}
                  disabled={disabled}
                  locale={locale}
                  onIconUpload={onIconUpload}
                />
              ) : (
                <>
                  {/* Icon upload (for icon groups only — legacy path, new Icon uses customComponent) */}
                  {iconField && (
                    <Box sx={{ mb: regularFields.length > 0 || section.paddingConfig ? 1.5 : 0 }}>
                      <MediaUploadField
                        value={extractIconUrl(values, iconField.path, locale) || null}
                        onChange={(val) =>
                          handleIconMediaChange(iconField.path, val)
                        }
                        mediaType="image"
                        showPreview={true}
                        size="small"
                        fullWidth
                        disabled={disabled}
                        placeholder="Icon image URL"
                      />
                    </Box>
                  )}

                  {/* Regular fields in 2-column grid */}
                  {regularFields.length > 0 && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: FIELD_STYLE_GRID_GAP,
                        mb: section.paddingConfig ? 1.5 : 0,
                      }}
                      data-testid={`button-style-section-${section.id}-fields`}
                    >
                      {regularFields.map((fieldConfig) => (
                        <StyleFieldRow
                          key={fieldConfig.path}
                          config={fieldConfig}
                          value={values[fieldConfig.path]}
                          onChange={(value) => onChange(fieldConfig.path, value)}
                          disabled={disabled}
                          locale={locale}
                          inputTestId={getStyleInputTestId(
                            section.id,
                            fieldConfig.label,
                          )}
                          checkboxTestId={
                            fieldConfig.isNullable
                              ? getStyleCheckboxTestId(
                                  section.id,
                                  fieldConfig.label,
                                )
                              : undefined
                          }
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}

              {/* Padding element — rendered for groups with paddingConfig.
                  For groups with objectPath (e.g., Icon), only show when the data object is non-null
                  (i.e., the feature is enabled via its customComponent checkbox). */}
              {section.paddingConfig &&
                (!section.objectPath || values[section.objectPath] != null) && (
                <PaddingElement
                  basePath={section.paddingConfig.basePath}
                  values={getPaddingValues(
                    values,
                    section.paddingConfig.basePath,
                  )}
                  onChange={handlePaddingChange}
                  label={section.paddingConfig.label}
                  disabled={disabled}
                  defaultExpanded={true}
                />
              )}

              {/* Placeholder for empty sections */}
              {!section.customComponent &&
                regularFields.length === 0 &&
                !iconField &&
                !section.paddingConfig && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontStyle: 'italic' }}
                  >
                    No settings
                  </Typography>
                )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default ButtonStyleSections;
